import { Moment } from "moment";
import { SelectV2 } from "PFComponents/select_v2";
import { getMonths } from "PFCore/helpers/date";
import { Option } from "PFTypes/options";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CalendarSelectProps } from "../calendar.types";

type MonthsProps = Pick<CalendarSelectProps, "displayMonth" | "setDisplayMonth"> & {
  minDate: Moment;
  maxDate: Moment;
};

export const Months = ({ minDate, maxDate, displayMonth, setDisplayMonth }: MonthsProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation("core", { keyPrefix: "components.calendar" });

  const monthsOptions: Option<number>[] = useMemo(
    () =>
      getMonths()
        .map((monthName, index) => {
          const month = displayMonth.clone().set("month", index);
          const isBefore = month.isBefore(minDate, "month");
          const isAfter = month.isAfter(maxDate, "month");
          const disabled = isBefore || isAfter;

          return {
            id: index,
            value: month.format("MMMM"),
            original: index,
            disabled
          };
        })
        .filter(({ disabled, id }) => !disabled || id === displayMonth.month()),
    [minDate, maxDate, displayMonth, language]
  );

  const selectedId = monthsOptions.find(({ id }) => id === displayMonth.month())?.id;

  const handleChange = useCallback(
    ({ id: value }: Option<number>) => setDisplayMonth(displayMonth.clone().set({ month: value as number })),
    [setDisplayMonth, displayMonth]
  );

  return (
    <SelectV2
      title={t("selectMonth")}
      qaId="calendar-select-month"
      value={selectedId}
      options={monthsOptions}
      onChange={handleChange}
      dropdownWidth={200}
      noPortal
    />
  );
};
